import * as Yup from "yup";
import PatientPersonalInfoValidation from "./patientPersonalInfo.validation";
import PatientContactInfoValidation from "./patientContactInfo.validation";
import OnlineOrderValuesModel from "../models/formModels/onlineOrderValues.model";
const isMinor = (birthDate: string | undefined): boolean => {
  if (!birthDate) return false;

  try {
    // Parse date in MM-DD-YYYY format
    const parts = birthDate.split("-");
    if (parts.length !== 3) return false;
    
    const [month, day, year] = parts.map(Number);
    
    // Validate the parsed numbers
    if (isNaN(month) || isNaN(day) || isNaN(year)) return false;
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return false;
    
    const birth = new Date(year, month - 1, day);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age < 18;
  } catch (error) {
    console.error("Error parsing birthDate:", error);
    return false;
  }
};

const additionalClinicianArraySchema = Yup.array().of(
  Yup.object().shape({
    firstName: Yup.string()
      .required("First Name is required!")
      .max(100, "First Name must be no longer than 100 characters"),
    lastName: Yup.string()
      .required("Last Name is required!")
      .max(100, "Last Name must be no longer than 100 characters"),
    fax: Yup.string()
      .test(
        "fax-validation",
        "Fax must contain 10 digits with the following format 000-000-0000",
        (value) => {
          if (!value) {
            return true; // Allow empty values
          }
          return /\d{3}-\d{3}-\d{4}/.test(value);
        }
      )
      .nullable(),
    npi: Yup.string()
      .nullable()
      .test("npi-validation", "NPI must be a 10-digit number", (value) => {
        if (!value) {
          return true;
        }
        return /^\d{10}$/.test(value);
      }),

    //  .required("NPI is required!"),
    email: Yup.string().email("Email ID is invalid!").nullable(),
  })
);

const additionalClinicianSchema = Yup.object().shape({
  additionalClinician: additionalClinicianArraySchema.when(
    "isAdditionalClinician",
    {
      is: (isAdditionalClinician: boolean) => isAdditionalClinician,
      then: (schema) =>
        schema.min(1).required("Additional Clinician is required!"),
    }
  ),
});

const billingInformationSchema = Yup.object().shape({
  additionalCodes: Yup.array().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 8,
    then: (schema) => schema.max(3, "Maximum 3 additional codes are allowed"),
  }),
 billingInfo: Yup.object()
  .shape({
    icdCodeId: Yup.number()
      .required("ICD code is required!")
      .typeError("Please select a valid ICD code"),
    primaryInsuranceId: Yup.mixed()
      .transform((value, originalValue) => originalValue === "" ? null : originalValue)
      .nullable()
      .test('is-valid-insurance', 'Please select a valid insurance', (value) => {
        return value === null || value === undefined || 
               (typeof value === 'number' && !isNaN(value));
      }),
    typeOfFacility: Yup.string().nullable(),
    dateOfDischarge: Yup.string().when(["typeOfFacility"], {
      is: (typeOfFacility: string) => typeOfFacility === "Hospital Inpatient",
      then: (schema) => schema.required("Date of discharge is required!"),
    }),
  })
  .when("testTypeId", {
    is: (testTypeId: number) => testTypeId !== 8,
    then: (schema) =>
      schema.shape({
        icdCodeId: Yup.number()
          .required("ICD code is required!")
          .typeError("Please select a valid ICD code"),
        primaryInsuranceId: Yup.mixed()
          .transform((value, originalValue) => originalValue === "" ? null : originalValue)
          .nullable()
          .test('is-valid-insurance', 'Please select a valid insurance', (value) => {
            return value === null || value === undefined || 
                   (typeof value === 'number' && !isNaN(value));
          }),
        typeOfFacility: Yup.string().required("Type of facility is required!"),
      }),
  }),
});

const laboratoryInformationSchema = Yup.object().shape({
  tumorSite: Yup.string()
    .when("testTypeId", {
      is: (testTypeId: number) => testTypeId === 5,
      then: (schema) => schema.required(),
    })
    .nullable(),
  tubeBarcode: Yup.string().when(["testTypeId", "isTeleHealth"], {
    is: (testTypeId: number, isTeleHealth: boolean) => {
      return testTypeId === 8 && !isTeleHealth;
    },
    then: (schema) =>
      schema
        .matches(
          /^\d[A-Z]{4}(-A|-B)/,
          "Tube Barcode can only have a digit as the first character, followed by four uppercase letters. It must end with '-A' or '-B'."
        )
        .required("Tube Barcode is required!"),
  }),
  facilityType: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => {
      return testTypeId === 8;
    },
    then: (schema) =>
      schema.required("Location of Sample Collection is required"),
  }),
  laboratoryInfo: Yup.object()
    .shape({
      labortaryName: Yup.string(),
      dateOfCollection: Yup.string(),
      typeOfSpecimen: Yup.string().required("Specimen type is required!"),
    })
    .when("testTypeId", {
      is: (testTypeId: number) => testTypeId !== 8 && testTypeId !== 9,
      then: (schema) =>
        schema.shape({
          labortaryName: Yup.string().required("Facility is required!"),
          dateOfCollection: Yup.string().required(
            "Date of collection is required!"
          ),
          typeOfSpecimen: Yup.string().required("Specimen type is required!"),
        }),
      /* .when("testTypeId", {
   is: (testTypeId: number) => testTypeId === 4 || testTypeId === 7,
   then: (schema) =>
    schema.shape({
     labortaryName: Yup.string().required("Facility is required!"),
     dateOfCollection: Yup.string(),
     typeOfSpecimen: Yup.string().required("Specimen type is required!"),
    }) */
      /* otherwise: (schema) =>
    schema.when("testTypeId", {
     is: (testTypeId: number) => testTypeId !== 8,
     then: (schema) =>
      schema.shape({
       labortaryName: Yup.string().required("Facility is required!"),
       dateOfCollection: Yup.string().required(
        "Date of collection is required!"
       ),
       typeOfSpecimen: Yup.string().required("Specimen type is required!"),
      }),
    }), */
    })
    .when("testTypeId", {
      is: (testTypeId: number) => testTypeId === 9,
      then: (schema) =>
        schema.shape({
          dateOfCollection: Yup.string().required(
            "Date of collection is required!"
          ),
          typeOfSpecimen: Yup.string().required("Specimen type is required!"),
          sampleLocationA: Yup.string()
            .required("Sample Location A is required!")
            .max(36, "Sample Location A must be no longer than 36 characters"),
          sampleLocationB: Yup.string()
            .required("Sample Location B is required!")
            .max(36, "Sample Location B must be no longer than 36 characters"),
        }),
    }),
});

const SCC_ClinicalInfoValidationSchema = Yup.object().shape({
  historyPhysicalExam: Yup.array()
    .of(Yup.string())
    .when(["testTypeId", "pathologySurgicalFindings"], {
      is: (testTypeId: number, pathologySurgicalFindings: string[]) =>
        testTypeId === 2 && pathologySurgicalFindings.length === 0,
      then: (schema) =>
        schema
          .min(
            1,
            " " /* "At least one option from any of the question must be selected!" */
          )
          .required(
            // "At least one option from any of the question must be selected!"
            " "
          ),
    }),
});

const AdvanceADClinicalInfoFieldsSchema = Yup.object().shape({
  isConsideredForTherapy: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 9,
    then: (schema) => schema.required("Answer is required!"),
  }),
});

const AdvanceADGuarantorFieldsSchema = Yup.object().shape(
  {
    testTypeId: Yup.number().nullable(),
    patientInfo: Yup.object().nullable(),
    guarantorName: Yup.string()
      .max(100, "Guarantor Name must not exceed 100 characters")
      .matches(/^[a-zA-Z\s]*$/, "Guarantor Name must contain only letters and spaces")
      .test('two-words', 'Guarantor Name must contain exactly two words separated by a single space', function(value) {
        if (!value) return true; // Allow empty values
        const words = value.trim().split(/\s+/);
        return words.length === 2 && words.every(word => word.length > 0);
      })
      .when(["testTypeId", "patientInfo"], {
        is: (testTypeId: number, patientInfo: any) => {
          // Only validate if testTypeId is 9 (AdvanceAD)
          if (testTypeId !== 9) return false;
          
          const birthDate = patientInfo?.birthDate;
          const result = isMinor(birthDate);
          // Log for debugging
          if (birthDate) {
            console.log("Validating guarantorName - testTypeId:", testTypeId, "birthDate:", birthDate, "isMinor:", result);
          }
          return result;
        },
        then: (schema) => schema.required("Guarantor Name is required!"),
        otherwise: (schema) => schema.nullable(),
      }),
  },
  [
    ["guarantorName", "testTypeId"],
    ["guarantorName", "patientInfo"],
  ]
);

const DX_MelanomaClinicalInfoFieldsSchema = Yup.object().shape({
  isSentinelLymphNodeBiopsy: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 3 || testTypeId === 6,
    then: (schema) => schema.required("Answer is required!"),
  }),
  isClinicallyPalpableNodes: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 3 || testTypeId === 6,
    then: (schema) => schema.required("Answer is required!"),
  }),
  isIntrasitMetastasis: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 3 || testTypeId === 6,
    then: (schema) => schema.required("Answer is required!"),
  }),
  lymphNodeStatus: Yup.string().when("isSentinelLymphNodeBiopsy", {
    is: (isSentinelLymphNodeBiopsy: string) =>
      isSentinelLymphNodeBiopsy === "Yes",
    then: (schema) => schema.required("lymphNodeStatus is required!"),
  }),
});

const MyPath_MelanomaClinicalInfoFieldsSchema = Yup.object().shape({
  isPrimaryCutaneousMelanocyticNeoplasm: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 5,
    then: (schema) =>
      schema
        .required("Answer is required!")
        .notOneOf(["No"], "Attestation is required in order to proceed!"),
  }),
});

const UM_UMSEQClinicianInfoFieldsSchema = Yup.object().shape({
  isExposedToRadiation: Yup.string().when("testTypeId", {
    is: (testTypeId: number) => testTypeId === 4 || testTypeId === 7,
    then: (schema) => schema.required("Answer is required!"),
  }),
  isReportPrame: Yup.string().when(["testTypeId", "secondaryTestId"], {
    is: (testTypeId: number, secondaryTestId: number) =>
      testTypeId === 4 || secondaryTestId === 4,
    then: (schema) => schema.required("Answer is required!"),
  }),
});

const patientHealthCareValidationSchema = Yup.object().shape({
  isAltheaDxNeuropsychotropicMedications: Yup.array()
    .of(Yup.string())
    .when("testTypeId", {
      is: (testTypeId: number) => testTypeId === 8,
      then: (schema) =>
        schema
          .min(
            1,
            "At least one option from any of the question must be selected!"
          )
          .required(
            "At least one option from any of the question must be selected!"
          ),
    }),
});

const patientInformationSchema = Yup.object().shape({
  patientInfo: Yup.object()
    .concat(PatientPersonalInfoValidation)
    .concat(PatientContactInfoValidation)
    .when("testTypeId", {
      is: (testTypeId: number) => testTypeId === 4 || testTypeId === 7,
      then: (schema) =>
        schema.shape({
          securityInfo: Yup.object().shape({
            ssn: Yup.string()
              .max(11)
              .matches(
                /\d{3}-\d{2}-\d{4}/,
                "SSN must contain 9 digits with following format 000-00-0000"
              )
              .when(["mrn"], {
                is: (mrn: string) => {
                  let regex = new RegExp(/^[a-zA-Z0-9]+$/);
                  return !mrn || mrn.length === 0 || !regex.test(mrn);
                },
                then: (schema) =>
                  schema
                    .required("SSN is required!")
                    .max(11)
                    .matches(
                      /\d{3}-\d{2}-\d{4}/,
                      "SSN must contain 9 digits with following format 000-00-0000"
                    ),
              }),
            mrn: Yup.string().matches(
              /^[a-zA-Z0-9]+$/,
              "MRN must be alphanumeric"
            ),
          }),
        }),
    }),
});
const supportingDocumentsSchema = Yup.object()
  .shape({
    notes: Yup.string().max(500, "Notes should not exceed 500 characters."),
    supportingDocuments: Yup.object().shape({
      clinicNotes: Yup.mixed().nullable(),
    }),
  })
  .test("insurance-document-required", function (formValues: any) {
    const primaryInsuranceId = Number(formValues?.billingInfo?.primaryInsuranceId);
    if (primaryInsuranceId !== 1) return true;

    const insuranceCard = formValues?.supportingDocuments?.insuranceCard;

    if (Array.isArray(insuranceCard)) {
      return insuranceCard.filter(Boolean).length > 0;
    }
    if (insuranceCard instanceof File) return true;
    if (typeof insuranceCard === "string") return insuranceCard.trim().length > 0;
    if (insuranceCard?.container && insuranceCard?.filename) return true;

    return this.createError({
      path: "supportingDocuments.insuranceCard",
      message:
        "Insurance document is required but could not be identified. Please upload insurance and classify again.",
    });
  });

const shipmentInformationSchema = Yup.object().shape({
  isTissueShipped: Yup.boolean(),
  shipTracking: Yup.string().when("isTissueShipped", {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.nullable(),
  }),
  shipMethod: Yup.string()
    .oneOf(["FedEx", "USPS", "UPS", "DHL", "Private Courier", "Hand-Delivered"])
    .when("isTissueShipped", {
      is: true,
      then: (schema) => schema.required("Shipment method is required!"),
      otherwise: (schema) => schema.nullable(),
    }),
});

const orderValidations = {
  additionalClinicianSchema,
  billingInformationSchema,
  laboratoryInformationSchema,
  patientHealthCareValidationSchema,
  SCC_ClinicalInfoValidationSchema,
  DX_MelanomaClinicalInfoFieldsSchema,
  MyPath_MelanomaClinicalInfoFieldsSchema,
  UM_UMSEQClinicianInfoFieldsSchema,
  patientInformationSchema,
  supportingDocumentsSchema,
  shipmentInformationSchema,
  AdvanceADClinicalInfoFieldsSchema,
  AdvanceADGuarantorFieldsSchema,
};

export default orderValidations;
